import React, { useEffect, useState } from "react";
import { AlertTriangle, Archive, Building2, CheckCircle2, GitMerge, Layers, Loader2, PowerOff, Trash2, Users, UserX } from "lucide-react";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Skeleton } from "./ui/skeleton";
import { api, errMsg } from "../lib/api";

const BUCKET_ICONS = {
  properties: Building2, properties_trash: Archive, staff_active: Users, staff_inactive: UserX, children: Layers, other: Layers,
};

/**
 * Phase 147 — Delete a master-data row.
 *   Loads GET /masters/{type}/{id}/dependencies and then either
 *     - offers "Delete permanently" (nothing active is linked), or
 *     - warns how many records are linked and offers "Deactivate instead" (soft delete).
 * Props: open, onOpenChange, code, masterType, item, nameField, onDeleted(), onDeactivated(), onMerge?()
 */
export const DeleteMasterDialog = ({ open, onOpenChange, code, masterType, item, nameField, onDeleted, onDeactivated, onMerge }) => {
  const [report, setReport] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !item) return;
    let cancelled = false;
    setReport(null);
    setLoadError(null);
    api.get(`/t/${code}/masters/${masterType}/${item.id}/dependencies`)
      .then((r) => { if (!cancelled) setReport(r.data); })
      .catch((e) => { if (!cancelled) setLoadError(errMsg(e)); });
    return () => { cancelled = true; };
  }, [open, item, code, masterType]);

  if (!item) return null;
  const name = report?.item?.name || (nameField ? item[nameField] : null) || item.id?.slice(0, 8);
  const singular = report?.singular || "record";
  const linked = (report?.buckets || []).filter((b) => b.count > 0);
  const blocking = linked.filter((b) => b.blocking);
  const nonBlocking = linked.filter((b) => !b.blocking);
  const isActive = report ? report.item.is_active : item.is_active;

  const permanentDelete = async () => {
    setBusy(true);
    try {
      const r = await api.delete(`/t/${code}/masters/${masterType}/${item.id}`);
      const s = r.data?.scrubbed || {};
      const extra = [s.properties_trash ? `${s.properties_trash} recycle-bin reference${s.properties_trash === 1 ? "" : "s"} cleared` : null,
        s.users ? `${s.users} inactive staff mapping${s.users === 1 ? "" : "s"} cleared` : null].filter(Boolean).join(", ");
      toast.success(`${singular} "${name}" deleted permanently${extra ? ` — ${extra}` : ""}`);
      onOpenChange(false);
      onDeleted?.();
    } catch (e) {
      const rep = e?.response?.data?.detail?.report;
      if (rep) { setReport(rep); toast.error(rep.message); }           // something got linked meanwhile
      else toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async () => {
    setBusy(true);
    try {
      await api.put(`/t/${code}/masters/${masterType}/${item.id}/toggle`);
      toast.success(`${singular} "${name}" deactivated — it stays on existing records and reports but is hidden from new entries`);
      onOpenChange(false);
      onDeactivated?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="sm:max-w-lg" data-testid="delete-master-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-destructive" /> Delete {singular.toLowerCase()}
          </DialogTitle>
          <DialogDescription data-testid="delete-master-subtitle">
            <span className="font-medium text-foreground">{name}</span>
            {item.parent_label ? <> · {item.parent_label}</> : null}
            {!isActive && <Badge variant="outline" className="ml-2 text-[10px]">Inactive</Badge>}
          </DialogDescription>
        </DialogHeader>

        {loadError && (
          <Alert variant="destructive" data-testid="delete-master-error">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Could not check linked records</AlertTitle>
            <AlertDescription>{loadError}</AlertDescription>
          </Alert>
        )}

        {!report && !loadError && (
          <div className="space-y-2" data-testid="delete-master-loading" aria-busy="true">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        )}

        {report && (
          <div className="space-y-3">
            {report.can_delete ? (
              <Alert data-testid="delete-master-ok" className="border-emerald-200 bg-emerald-50/60 text-emerald-900 [&>svg]:text-emerald-600">
                <CheckCircle2 className="h-4 w-4" />
                <AlertTitle>{report.linked_total ? "No active records are linked" : "Nothing is linked"}</AlertTitle>
                <AlertDescription className="text-emerald-900/80">{report.message}</AlertDescription>
              </Alert>
            ) : (
              <Alert data-testid="delete-master-blocked" className="border-amber-300 bg-amber-50 text-amber-900 [&>svg]:text-amber-600">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>
                  {report.guards?.length ? "Cannot be deleted" : <>{report.blocking_total.toLocaleString("en-IN")} active record{report.blocking_total === 1 ? " is" : "s are"} linked to this {singular.toLowerCase()}</>}
                </AlertTitle>
                <AlertDescription className="text-amber-900/80">
                  {report.guards?.length ? report.guards[0]
                    : "Permanent deletion is only possible when nothing active is linked. You can deactivate it instead — it stays on existing records and in reports but disappears from dropdowns for new entries."}
                </AlertDescription>
              </Alert>
            )}

            {linked.length > 0 && (
              <ul className="rounded-lg border divide-y text-sm" data-testid="delete-master-linked-list">
                {linked.map((b) => {
                  const Icon = BUCKET_ICONS[b.key] || Layers;
                  return (
                    <li key={b.key} className="flex items-start justify-between gap-3 px-3 py-2" data-testid={`delete-master-bucket-${b.key}`}>
                      <div className="flex items-start gap-2 min-w-0">
                        <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${b.blocking ? "text-amber-600" : "text-muted-foreground"}`} />
                        <div className="min-w-0">
                          <p className="font-medium leading-tight">{b.label}</p>
                          {b.collections?.length > 0 && (b.key === "other" || b.key === "children") && (
                            <p className="text-xs text-muted-foreground truncate">
                              {b.collections.map((c) => `${c.count.toLocaleString("en-IN")} ${c.label}`).join(" · ")}
                            </p>
                          )}
                          {!b.blocking && <p className="text-xs text-muted-foreground">Does not block — cleared on permanent delete</p>}
                        </div>
                      </div>
                      <span className={`tabular-nums font-semibold ${b.blocking ? "text-amber-700" : "text-muted-foreground"}`} data-testid={`delete-master-count-${b.key}`}>
                        {b.count.toLocaleString("en-IN")}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}

            {!report.can_delete && report.mergeable && onMerge && (
              <p className="text-xs text-muted-foreground flex items-start gap-1.5" data-testid="delete-master-merge-tip">
                <GitMerge className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>
                  Need it gone for good? <button type="button" className="underline underline-offset-2 hover:text-foreground" onClick={() => { onOpenChange(false); onMerge(item); }} data-testid="delete-master-merge-link">
                    Merge it into another {singular.toLowerCase()}</button> — every linked record moves across and this one is removed.
                </span>
              </p>
            )}
            {!report.can_delete && !isActive && (
              <p className="text-xs text-muted-foreground" data-testid="delete-master-already-inactive">
                This {singular.toLowerCase()} is already inactive. Unlink or move the records above to delete it permanently.
              </p>
            )}
            {report.can_delete && nonBlocking.length > 0 && blocking.length === 0 && (
              <p className="text-xs text-muted-foreground" data-testid="delete-master-scrub-note">
                Recycle-bin snapshots and inactive staff mappings that point here will be cleared so nothing refers to a missing {singular.toLowerCase()}.
              </p>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="delete-master-cancel">Cancel</Button>
          {report?.can_delete && (
            <Button variant="destructive" onClick={permanentDelete} disabled={busy} data-testid="delete-master-confirm">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />} Delete permanently
            </Button>
          )}
          {report && !report.can_delete && isActive && (
            <Button onClick={deactivate} disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="delete-master-deactivate">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PowerOff className="h-4 w-4 mr-2" />} Deactivate instead
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
